// Dashboard API Service
const API_BASE_URL = 'https://apisaleeasy.psisat.com/api/analytics';

export const dashboardService = {
  // Get elite hourly sales data
  async getHourlySales(dateFrom, dateTo) {
    try {
      const url = `${API_BASE_URL}/product-sales/elite-hourly-sales?dateFrom=${dateFrom}&dateTo=${dateTo}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        return {
          success: true,
          data: data.data,
          executive_summary: data.executive_summary,
          insights: data.insights,
          request_context: data.request_context
        };
      } else {
        throw new Error(data.message || 'Failed to fetch elite hourly sales data');
      }
    } catch (error) {
      console.error('Error fetching elite hourly sales data:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  },

  // Get top products data
  async getTopProducts(dateFrom, dateTo, limit = 15) {
    try {
      const url = `${API_BASE_URL}/product-sales/top-products?dateFrom=${dateFrom}&dateTo=${dateTo}&limit=${limit}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        return {
          success: true,
          data: data.data,
          filters: data.filters
        };
      } else {
        throw new Error(data.message || 'Failed to fetch top products data');
      }
    } catch (error) {
      console.error('Error fetching top products data:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  },

  // Get elite daily sales data
  async getDailySales(dateFrom, dateTo) {
    try {
      const url = `${API_BASE_URL}/product-sales/elite-daily-sales?dateFrom=${dateFrom}&dateTo=${dateTo}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        return {
          success: true,
          data: data.data,
          executive_summary: data.executive_summary,
          insights: data.insights,
          request_context: data.request_context
        };
      } else {
        throw new Error(data.message || 'Failed to fetch elite daily sales data');
      }
    } catch (error) {
      console.error('Error fetching elite daily sales data:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  },

  // Format date for API (YYYY-MM-DD format) — use local date parts to avoid UTC timezone shift
  formatDateForAPI(date) {
    if (!date) return '';
    const d = new Date(date);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  },

  // Get factory type for a product
  async getFactoryType(productCode) {
    try {
      const response = await fetch('https://apisaleeasy.psisat.com/api/factory/type', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          "num_name_products": productCode.trim()
        })
      });

      if (!response.ok) {
        return { factory_type: 'Other' }; // Default to 'Other' if not found
      }

      const data = await response.json();
      
      if (data.status && data.data) {
        return {
          factory_type: data.data.factory_type || 'Other',
          factory_name_products: data.data.factory_name_products || '',
        };
      } else {
        return { factory_type: 'Other' }; // Default to 'Other' if not found
      }
    } catch (error) {
      console.error(`Error fetching factory type for ${productCode}:`, error);
      return { factory_type: 'Other' }; // Default to 'Other' on error
    }
  },

  // Get factory types for multiple products
  async getFactoryTypesForProducts(products) {
    if (!products || products.length === 0) return {};

    const factoryTypePromises = products.map(async (product) => {
      const factoryInfo = await this.getFactoryType(product.product_code);
      return {
        product_code: product.product_code.trim(),
        factory_type: factoryInfo.factory_type,
        factory_name_products: factoryInfo.factory_name_products
      };
    });

    const results = await Promise.all(factoryTypePromises);
    
    const factoryTypeMap = {};
    results.forEach(result => {
      factoryTypeMap[result.product_code] = {
        factory_type: result.factory_type,
        factory_name_products: result.factory_name_products
      };
    });
    
    return factoryTypeMap;
  },

  // Get default date range (last 7 days)
  getDefaultDateRange() {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 7);
    
    return {
      startDate: this.formatDateForAPI(startDate),
      endDate: this.formatDateForAPI(endDate)
    };
  }
};

export default dashboardService;